import { useEffect, useMemo, useRef, useState } from "react";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, RadialBar, RadialBarChart,
} from "recharts";
import { motion } from "framer-motion";
import {
  Sparkles, FileDown, Loader2, RefreshCw, Info, TrendingUp, TrendingDown,
  Award, ShieldAlert, CheckCircle2, AlertTriangle, Shield, Target, Zap,
  Users, GraduationCap, MessageSquare, ChevronRight, Lock, Unlock, History,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  DimensionBar, AxisBar, IndexCard, ScenarioCard, AnalysisBlock,
  AXIS_LABELS, INDEX_LABELS,
} from "@/components/diagnostic/DiagnosticBlocks";
import VaultEvidenceMap from "@/components/VaultEvidenceMap";

/**
 * Enriched IMES™ diagnostic panel embedded on the dossier detail page.
 *
 * Mirrors the visual richness of `/app/diagnostic/report` (personal) so a
 * professional studying a shadow-client dossier gets the same read : IMES
 * v2 decomposition, 8 axes, 6 indices, 4 scenarios, AI analysis blocks.
 */

const AXES = Object.entries(AXIS_LABELS).map(([key, label]) => ({ key, label }));

const SUB_SCORES = [
  { key: "origine_score",         label: "Origine",     weight: "20 %", tone: "from-amber-400 to-amber-600" },
  { key: "present_composite",     label: "Présent",     weight: "40 %", tone: "from-[#0B2545] to-blue-700" },
  { key: "trajectoire_score",     label: "Trajectoire", weight: "30 %", tone: "from-emerald-400 to-emerald-600" },
  { key: "resilience_multi_axes", label: "Résilience",  weight: "10 %", tone: "from-rose-400 to-rose-600" },
];

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("fr-CA", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function gradeColor(grade) {
  const g = (grade || "").toUpperCase();
  if (g.startsWith("A")) return "#059669";
  if (g.startsWith("B")) return "#65A30D";
  if (g.startsWith("C")) return "#C5A880";
  if (g.startsWith("D")) return "#D97706";
  return "#B91C1C";
}

export function DossierImesSection({ dossier, onDossierUpdate }) {
  const dossierId = dossier?.dossier_id;
  const stub = dossier?.economic_data?.diagnostic_imes || null;

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [aiStatus, setAiStatus] = useState(null);
  const [legal, setLegal] = useState("");
  const [locking, setLocking] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showForceConfirm, setShowForceConfirm] = useState(false);
  const pollTimerRef = useRef(null);

  const fetchReport = async () => {
    if (!dossierId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/diagnostic/dossier/${dossierId}/report`);
      setReport(data.diagnostic || null);
      setLegal(data.legal_disclaimer_fr || "");
      const st = data.diagnostic?.analysis_status;
      setAiStatus(st || (data.diagnostic?.ai_analysis ? "completed" : null));
    } catch {
      setReport(null);
    } finally { setLoading(false); }
  };

  const fetchHistory = async () => {
    if (!dossierId) return;
    try {
      const { data } = await api.get(`/diagnostic/dossier/${dossierId}/history`);
      setHistory(data.items || []);
    } catch { /* no-op */ }
  };

  useEffect(() => {
    if (stub?.last_diagnostic_id || stub?.last_score != null) fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dossierId, stub?.last_diagnostic_id]);

  useEffect(() => {
    if (aiStatus !== "running" || !dossierId) {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      return;
    }
    let attempts = 0;
    pollTimerRef.current = setInterval(async () => {
      attempts += 1;
      if (attempts > 60) { clearInterval(pollTimerRef.current); return; }
      try {
        const { data } = await api.get(`/diagnostic/dossier/${dossierId}/analyze/status`);
        if (data.analysis_status === "completed" || data.analysis_status === "failed") {
          clearInterval(pollTimerRef.current);
          setAiStatus(data.analysis_status);
          await fetchReport();
        }
      } catch { /* keep polling */ }
    }, 3000);
    return () => { if (pollTimerRef.current) clearInterval(pollTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiStatus, dossierId]);

  const scores = report?.scores || null;
  const scenarios = report?.scenarios || [];
  const ai = report?.ai_analysis || {};
  const radarData = useMemo(() => {
    if (!scores) return [];
    return AXES.map((a) => ({ axis: a.label, value: Number(scores[a.key] || 0) }));
  }, [scores]);

  const runAnalyze = async (opts = {}) => {
    if (!dossierId) return;
    const force = !!opts.force;
    setRunning(true);
    try {
      const url = force
        ? `/diagnostic/dossier/${dossierId}/analyze?force=true`
        : `/diagnostic/dossier/${dossierId}/analyze`;
      const { data } = await api.post(url);
      const s = data?.scores?.imes;
      if (data?.cached) {
        toast.success(`Cache — aucune donnée n'a changé depuis la dernière analyse (${Number(s || 0).toFixed(1)}/100).`);
      } else {
        toast.success(s != null
          ? `Diagnostic IMES™ calculé — score ${Number(s).toFixed(1)}/100.`
          : "Diagnostic IMES™ lancé.");
      }
      if (!data?.cached) setAiStatus("running");
      await fetchReport();
      onDossierUpdate?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      if (status === 409) {
        setShowForceConfirm(true);
        return;
      }
      toast.error(typeof detail === "string" ? detail
        : "Impossible de lancer l'IMES — complétez d'abord le questionnaire.");
    } finally { setRunning(false); }
  };

  const toggleLock = async () => {
    if (!dossierId) return;
    setLocking(true);
    try {
      const endpoint = report?.is_locked ? "unlock" : "lock";
      const { data } = await api.post(`/diagnostic/dossier/${dossierId}/${endpoint}`);
      toast.success(report?.is_locked
        ? "Analyse déverrouillée."
        : "Analyse verrouillée comme version de référence.");
      await fetchReport();
      if (data.diagnostic_id) await fetchHistory();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Action impossible.");
    } finally { setLocking(false); }
  };

  const downloadPdf = async () => {
    if (!dossierId) return;
    setDownloading(true);
    try {
      const resp = await api.get(`/diagnostic/dossier/${dossierId}/pdf`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `altiora-imes-${dossierId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Export PDF indisponible.");
    } finally { setDownloading(false); }
  };

  // ── Empty state ──────────────────────────────────────────────────────
  if (!loading && !scores) {
    return (
      <section className="mt-12" data-testid="dossier-imes-section">
        <SectionHeader />
        <div className="mt-6 rounded-2xl border border-dashed border-[#C5A880]/60 bg-white/70 p-8 md:p-10 text-center"
             data-testid="dossier-imes-empty">
          <div className="mx-auto h-12 w-12 rounded-full bg-[#FBF6EA] flex items-center justify-center">
            <TrendingUp className="h-6 w-6 text-[#7A5B14]" strokeWidth={1.5} />
          </div>
          <h3 className="mt-4 font-display text-xl text-[#1F2937] tracking-tight">
            Aucun diagnostic IMES™ lancé
          </h3>
          <p className="mt-2 text-sm text-[#5B5B62] max-w-lg mx-auto leading-relaxed">
            L&apos;Indice de Mobilité Économico-Sociale calcule 8 axes de trajectoire, 6 indices
            synthétiques, 4 scénarios projetés et une analyse IA. Complétez au moins la
            section « Emploi &amp; revenus » ou « Parcours professionnel » avant de lancer.
          </p>
          <button onClick={() => runAnalyze()} disabled={running} data-testid="dossier-imes-run"
                  className="mt-6 inline-flex items-center gap-2 rounded-md bg-[#0B2545] hover:bg-[#123163] disabled:opacity-60 text-white px-5 py-2.5 text-sm font-medium transition-colors">
            {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />
                     : <Sparkles className="h-3.5 w-3.5" strokeWidth={2} />}
            {running ? "Calcul…" : "Lancer le diagnostic IMES™"}
          </button>
        </div>
      </section>
    );
  }

  if (loading && !scores) {
    return (
      <section className="mt-12" data-testid="dossier-imes-section">
        <SectionHeader />
        <div className="mt-6 rounded-2xl border border-[#E4E1D8] bg-white p-8 flex items-center justify-center text-[#5B5B62] text-sm">
          <Loader2 className="h-4 w-4 animate-spin mr-2" strokeWidth={1.5} /> Chargement du diagnostic…
        </div>
      </section>
    );
  }

  const imes = Number(scores.imes || 0);
  const grade = scores.imes_grade || "";
  const color = gradeColor(grade);
  const hasAi = ai?.generated_at;

  return (
    <section className="mt-12 space-y-8" data-testid="dossier-imes-section">
      <SectionHeader subtitle={report?.completed_at ? `Dernier calcul · ${formatDate(report.completed_at)}${report?.input_hash ? ` · empreinte ${String(report.input_hash).slice(0,8)}` : ""}` : null} />

      {/* Force-relaunch confirmation banner (shown when locked and user tried to rerun) */}
      {showForceConfirm && (
        <div className="rounded-md border-2 border-[#7A5B14] bg-[#FBF6EA] px-4 py-3 flex items-start gap-3"
             data-testid="dossier-imes-force-confirm">
          <ShieldAlert className="h-5 w-5 text-[#7A5B14] flex-shrink-0 mt-0.5" strokeWidth={2} />
          <div className="flex-1">
            <div className="font-display text-sm text-[#5A4308]">Analyse verrouillée</div>
            <p className="mt-0.5 text-xs text-[#7A5B14] leading-relaxed">
              Cette analyse a été verrouillée comme version de référence. Générer une nouvelle version
              conservera l&apos;ancienne dans l&apos;historique.
            </p>
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => { setShowForceConfirm(false); runAnalyze({ force: true }); }}
                data-testid="dossier-imes-force-confirm-yes"
                className="inline-flex items-center gap-1.5 rounded-md bg-[#7A5B14] hover:bg-[#5A4308] text-white px-3 py-1.5 text-xs font-medium"
              >
                Générer une nouvelle version
              </button>
              <button
                onClick={() => setShowForceConfirm(false)}
                data-testid="dossier-imes-force-confirm-no"
                className="inline-flex items-center gap-1.5 rounded-md border border-[#7A5B14]/40 bg-white text-[#7A5B14] px-3 py-1.5 text-xs font-medium"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History panel */}
      {showHistory && (
        <div className="rounded-lg border border-[#E4E1D8] bg-white p-4" data-testid="dossier-imes-history-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display text-sm text-[#0B2545]">Historique des analyses</div>
            <button onClick={() => setShowHistory(false)} className="text-xs text-[#5B5B62] hover:text-[#0B2545]">
              Fermer
            </button>
          </div>
          {history.length === 0 ? (
            <div className="text-xs text-[#5B5B62] py-2">Aucune analyse antérieure.</div>
          ) : (
            <ul className="space-y-1.5">
              {history.map((h) => (
                <li
                  key={h.id}
                  className={`text-xs flex items-center gap-3 px-3 py-2 rounded ${h.is_locked ? "bg-[#FBF6EA]/60 border border-[#C5A880]/50" : "border border-[#E4E1D8]"}`}
                  data-testid={`dossier-imes-history-${h.id}`}
                >
                  <span className="font-mono text-[#5B5B62] tabular-nums">{formatDate(h.completed_at)}</span>
                  <span className="font-mono text-[#0B2545] tabular-nums">
                    {h.scores?.imes != null ? `${Number(h.scores.imes).toFixed(1)}/100` : "—"}
                    {h.scores?.imes_grade ? ` · ${h.scores.imes_grade}` : ""}
                  </span>
                  {h.is_locked && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#7A5B14]">
                      <Lock className="h-2.5 w-2.5" /> verrouillée
                    </span>
                  )}
                  {h.input_hash && (
                    <span className="ml-auto text-[10px] font-mono text-[#8B8B8F]">
                      {String(h.input_hash).slice(0, 8)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Async AI banner */}
      {(aiStatus === "running" || aiStatus === "queued") && !hasAi && (
        <div className="rounded-md border border-[#C5A880] bg-[#FBF6EA] px-4 py-3 flex items-center gap-3"
             data-testid="dossier-imes-ai-running-banner">
          <Loader2 className="h-4 w-4 text-[#7A5B14] animate-spin" />
          <div className="text-sm text-[#5A4308]">
            L&apos;analyse Claude Sonnet se termine en arrière-plan — la lecture narrative apparaîtra automatiquement dans quelques secondes.
          </div>
        </div>
      )}

      {/* Hero card — gauge + décomposition v2 */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
                  className="rounded-2xl border-2 border-[#0B2545]/25 bg-white shadow-sm overflow-hidden"
                  data-testid="dossier-imes-hero">
        {/* Header actions */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 border-b border-[#E4E1D8] bg-[#FBF6EA]/40 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-[#7A5B14]/10 flex items-center justify-center">
              <Award className="h-4 w-4 text-[#7A5B14]" strokeWidth={1.8} />
            </div>
            <div>
              <div className="font-display text-base text-[#1F2937] tracking-tight">Indice de mobilité économico-sociale</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">
                Méthode {scores.method_version || "ALTIORA-METHOD-IMES-v2.0.0"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {report?.is_locked && (
              <span
                className="inline-flex items-center gap-1.5 rounded-full border border-[#7A5B14]/40 bg-[#FBF6EA] text-[#7A5B14] px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest"
                data-testid="dossier-imes-locked-badge"
                title={report.locked_at ? `Verrouillée le ${formatDate(report.locked_at)}` : ""}
              >
                <Lock className="h-3 w-3" strokeWidth={2} /> Verrouillée
              </span>
            )}
            <button
              onClick={() => {
                if (!showHistory) fetchHistory();
                setShowHistory((v) => !v);
              }}
              data-testid="dossier-imes-history-toggle"
              className="inline-flex items-center gap-1.5 rounded-md border border-[#E4E1D8] bg-white hover:border-[#0B2545] hover:text-[#0B2545] text-[#1F2937] px-3 py-2 text-xs font-medium transition-colors"
            >
              <History className="h-3 w-3" strokeWidth={2} /> Historique
            </button>
            <button
              onClick={toggleLock}
              disabled={locking}
              data-testid="dossier-imes-lock-toggle"
              className="inline-flex items-center gap-1.5 rounded-md border border-[#E4E1D8] bg-white hover:border-[#0B2545] hover:text-[#0B2545] disabled:opacity-60 text-[#1F2937] px-3 py-2 text-xs font-medium transition-colors"
            >
              {locking ? <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} />
                       : report?.is_locked
                          ? <Unlock className="h-3 w-3" strokeWidth={2} />
                          : <Lock className="h-3 w-3" strokeWidth={2} />}
              {report?.is_locked ? "Déverrouiller" : "Verrouiller"}
            </button>
            <button onClick={() => runAnalyze()} disabled={running} data-testid="dossier-imes-rerun"
                    className="inline-flex items-center gap-1.5 rounded-md border border-[#E4E1D8] bg-white hover:border-[#0B2545] hover:text-[#0B2545] disabled:opacity-60 text-[#1F2937] px-3.5 py-2 text-xs font-medium transition-colors">
              {running ? <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} />
                       : <RefreshCw className="h-3 w-3" strokeWidth={2} />}
              {running ? "Relance…" : "Relancer"}
            </button>
            <button onClick={downloadPdf} disabled={downloading} data-testid="dossier-imes-pdf"
                    className="inline-flex items-center gap-1.5 rounded-md bg-[#0B2545] hover:bg-[#123163] disabled:opacity-60 text-white px-3.5 py-2 text-xs font-medium transition-colors">
              {downloading ? <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} />
                           : <FileDown className="h-3 w-3" strokeWidth={2} />}
              PDF
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 p-6 md:p-8">
          {/* Gauge + grade */}
          <div className="flex flex-col items-center justify-start">
            <div className="relative">
              <RadialBarChart width={240} height={240} cx="50%" cy="50%"
                              innerRadius="76%" outerRadius="100%"
                              barSize={14} startAngle={90} endAngle={-270}
                              data={[{ name: "imes", value: Math.max(0, Math.min(100, imes)) }]}>
                <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
                <RadialBar background={{ fill: "#EBE8DE" }} dataKey="value" cornerRadius={4} fill={color} />
              </RadialBarChart>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <div className="font-mono text-5xl font-medium text-[#1F2937] tabular-nums" data-testid="dossier-imes-score">
                  {imes.toFixed(1)}
                </div>
                <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#5B5B62]">/ 100</div>
                {grade && (
                  <div className="mt-2 rounded-full px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-widest"
                       style={{ backgroundColor: `${color}18`, color }} data-testid="dossier-imes-grade">
                    Grade {grade}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Décomposition v2 */}
          <div>
            <h3 className="font-display text-lg text-[#0B2545] mb-1">Décomposition IMES v2</h3>
            <p className="text-xs text-[#5B5B62] mb-4">
              4 composantes pondérées selon la Doctrine ALTIORA Méthode™ v2.
            </p>
            <div className="space-y-3" data-testid="dossier-imes-decomposition">
              {SUB_SCORES.map((s) => (
                <DimensionBar key={s.key} label={`${s.label} — ${labelHint(s.key)}`}
                              value={Number(scores[s.key] || 0)} weight={s.weight} tone={s.tone} />
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Axes + indices */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-6">
        {/* Radar + 8 axes bars */}
        <div className="border-2 border-[#E5E7EB] bg-white rounded-lg p-6" data-testid="dossier-imes-axes">
          <h3 className="font-display text-lg text-[#0B2545] mb-4">8 axes multidimensionnels</h3>
          <div className="h-[240px] w-full mb-4" data-testid="dossier-imes-radar">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData} outerRadius="72%">
                <PolarGrid stroke="#E4E1D8" />
                <PolarAngleAxis dataKey="axis" tick={{ fill: "#1F2937", fontSize: 11 }} />
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: "#B8B5AC", fontSize: 9 }} axisLine={false} />
                <Radar dataKey="value" stroke="#0B2545" fill="#C5A880" fillOpacity={0.35} strokeWidth={1.8} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2">
            {AXES.map((a) => (
              <div key={a.key} data-testid={`dossier-imes-axis-${a.key}`}>
                <AxisBar label={a.label} value={Number(scores[a.key] || 0)} />
              </div>
            ))}
          </div>
        </div>

        {/* 6 Indices synthétiques */}
        <div className="border-2 border-[#E5E7EB] bg-white rounded-lg p-6" data-testid="dossier-imes-indices">
          <h3 className="font-display text-lg text-[#0B2545] mb-4">Indices synthétiques</h3>
          <p className="text-xs text-[#5B5B62] mb-4">
            Six lectures macro dérivées des 8 axes — pour comparer profils et suivre l&apos;évolution.
          </p>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(INDEX_LABELS).map(([k, label]) => (
              <div key={k} data-testid={`dossier-imes-index-${k}`}>
                <IndexCard label={label} value={Number(scores[k] || 0)} isRisk={k === "idx_risk"} />
              </div>
            ))}
          </div>

          {/* Dynamics v2 (if present) */}
          {(scores.velocity_score != null || scores.momentum_score != null || scores.stability_score != null) && (
            <div className="mt-5 pt-5 border-t border-[#E4E1D8]">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A5B14] mb-3">Dynamiques (v2)</div>
              <div className="grid grid-cols-2 gap-3">
                {scores.velocity_score != null && <IndexCard label="Vélocité" value={Number(scores.velocity_score)} />}
                {scores.momentum_score != null && <IndexCard label="Momentum" value={Number(scores.momentum_score)} />}
                {scores.stability_score != null && <IndexCard label="Stabilité dyn." value={Number(scores.stability_score)} />}
                {scores.consistency_score != null && <IndexCard label="Consistance" value={Number(scores.consistency_score)} />}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 4 scénarios projetés */}
      {scenarios.length > 0 && (
        <div data-testid="dossier-imes-scenarios">
          <h3 className="font-display text-xl text-[#0B2545] mb-2">4 trajectoires projetées</h3>
          <p className="text-sm text-[#5B5B62] mb-4 max-w-3xl">
            Projections calculées à partir des données actuelles et de la capacité d&apos;action déclarée du client — horizons 1/3/5/10 ans.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {scenarios.map((sc) => <ScenarioCard key={sc.key} scenario={sc} />)}
          </div>
        </div>
      )}

      {/* Analyse IA (Claude Sonnet 4.5) */}
      {hasAi ? (
        <div data-testid="dossier-imes-ai">
          <h3 className="font-display text-xl text-[#0B2545] mb-2">Lecture ALTIORA · Analyse IA</h3>
          <p className="text-sm text-[#5B5B62] mb-4">
            Générée par Claude Sonnet 4.5 · {formatDate(ai.generated_at)}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <AnalysisBlock icon={CheckCircle2} title="Forces"           items={ai.strengths_fr}         tone="emerald" />
            <AnalysisBlock icon={AlertTriangle} title="Vulnérabilités"  items={ai.vulnerabilities_fr}   tone="amber" />
            <AnalysisBlock icon={Sparkles}     title="Opportunités"     items={ai.opportunities_fr}     tone="blue" />
            <AnalysisBlock icon={Shield}       title="Risques"          items={ai.risks_fr}             tone="red" />
            <AnalysisBlock icon={Target}       title="Priorités"        items={ai.priorities_fr}        tone="navy" wide />
            <AnalysisBlock icon={Zap}          title="Gains rapides"    items={ai.quick_wins_fr}        tone="emerald" />
            <AnalysisBlock icon={TrendingUp}   title="Gains long terme" items={ai.long_term_wins_fr}    tone="blue" />
            <AnalysisBlock icon={ChevronRight} title="Recommandations"  items={ai.recommendations_fr}   tone="navy" wide />
            {ai.recommended_partners_fr?.length > 0  && <AnalysisBlock icon={Users}         title="Partenaires suggérés"        items={ai.recommended_partners_fr}  tone="gold" />}
            {ai.recommended_programs_fr?.length > 0  && <AnalysisBlock icon={Award}         title="Programmes gouvernementaux"  items={ai.recommended_programs_fr}  tone="gold" />}
            {ai.recommended_trainings_fr?.length > 0 && <AnalysisBlock icon={GraduationCap} title="Formations recommandées"     items={ai.recommended_trainings_fr} tone="gold" />}
            {ai.missing_info_fr?.length > 0          && <AnalysisBlock icon={MessageSquare} title="Informations à préciser"     items={ai.missing_info_fr}          tone="grey" />}
          </div>
        </div>
      ) : (aiStatus === "failed" || ai?.error_fr) ? (
        <div className="border-2 border-dashed border-red-300 bg-red-50/40 rounded-lg p-6" data-testid="dossier-imes-ai-failed">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-700 mt-0.5" strokeWidth={1.8} />
            <div>
              <div className="font-display text-base text-red-900">L&apos;analyse IA a échoué</div>
              <p className="mt-1 text-sm text-red-800">
                Les scores quantitatifs restent valides. {ai?.error_fr || "Relancez le diagnostic pour un nouvel essai."}
              </p>
              <button onClick={runAnalyze} disabled={running}
                      className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-red-800 hover:bg-red-900 text-white px-3 py-1.5 text-xs font-medium">
                <RefreshCw className="h-3 w-3" /> Relancer
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Legal disclaimer */}
      {legal && (
        <div className="border border-dashed border-[#C5A880]/60 bg-[#FBF6EA]/70 rounded-md p-4 flex items-start gap-2">
          <ShieldAlert className="h-4 w-4 text-[#7A5B14] flex-shrink-0 mt-0.5" strokeWidth={2} />
          <div className="text-xs text-[#7A5B14] leading-relaxed">{legal}</div>
        </div>
      )}

      {/* Vault evidence map — cite the documents that support each capital */}
      <VaultEvidenceMap dossierId={dossierId} engine="imes" />
    </section>
  );
}

function labelHint(key) {
  switch (key) {
    case "origine_score":         return "d'où vient le client";
    case "present_composite":     return "situation composite actuelle";
    case "trajectoire_score":     return "direction projetée";
    case "resilience_multi_axes": return "maillon le plus faible";
    default: return "";
  }
}

function SectionHeader({ subtitle }) {
  return (
    <div className="flex items-baseline justify-between">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-[#7A5B14]">Diagnostic IMES™</div>
        <h2 className="mt-1 font-display text-2xl tracking-tight text-[#1F2937]">Trajectoire économico-sociale</h2>
        {subtitle && (
          <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">{subtitle}</div>
        )}
      </div>
    </div>
  );
}

export default DossierImesSection;
